import { ClassicTemplate } from './templates/ClassicTemplate.js';
// DashboardView is lazy-imported on first tab click to keep Chart.js/Leaflet out of main bundle

const TEMPLATES = {
  classic: ClassicTemplate,
};

/**
 * ResumePreview — the main preview container with Resume/Dashboard tab toggle.
 *
 * Resume tab: renders the selected template.
 * Dashboard tab: renders the DashboardView with charts.
 *
 * Dashboard is created once on first tab click (lazy), then shown/hidden via CSS.
 */
export class ResumePreview {
  constructor() {
    this._wrapper = null;
    this._cardContainer = null;
    this._dashboardContainer = null;
    this._dashboardView = null;
    this._activeTab = 'resume';
    this._tabBar = null;
    this._latestRaces = [];
  }

  render(container) {
    this._wrapper = document.createElement('div');
    this._wrapper.id = 'resume-preview';
    this._wrapper.className = 'preview-panel';

    // Tab bar
    this._tabBar = document.createElement('div');
    this._tabBar.className = 'preview-tabs';
    this._tabBar.innerHTML = `
      <button class="preview-tabs__btn preview-tabs__btn--active" data-tab="resume">Resume</button>
      <button class="preview-tabs__btn" data-tab="dashboard">Dashboard</button>
    `;
    this._tabBar.addEventListener('click', (e) => {
      const tab = e.target.dataset?.tab;
      if (tab && tab !== this._activeTab) this._switchTab(tab);
    });
    this._wrapper.appendChild(this._tabBar);

    // Resume card
    this._cardContainer = document.createElement('div');
    this._cardContainer.id = 'resume-card';
    this._cardContainer.className = 'preview-card';
    this._cardContainer.innerHTML = `
      <div class="preview-empty">
        <span class="preview-empty__icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
        </span>
        <p class="preview-empty__text">Your resume preview will appear here</p>
      </div>
    `;
    this._wrapper.appendChild(this._cardContainer);

    // Dashboard container (hidden by default, created lazily)
    this._dashboardContainer = document.createElement('div');
    this._dashboardContainer.id = 'dashboard-pane';
    this._dashboardContainer.style.display = 'none';
    this._dashboardContainer.style.width = '100%';
    this._wrapper.appendChild(this._dashboardContainer);

    container.appendChild(this._wrapper);
  }

  /**
   * Update resume preview. Also stores races for dashboard.
   */
  update({ resume, stats, templateId }) {
    if (!this._cardContainer) return;

    this._latestRaces = resume.races || [];

    const TemplateClass = TEMPLATES[templateId] || TEMPLATES.classic;
    const highlighted = this._latestRaces.filter(r => r.isHighlighted);
    const races = highlighted.length > 0 ? highlighted : this._latestRaces;

    this._cardContainer.innerHTML = '';
    const cardEl = TemplateClass.render({ resume, stats, races });
    this._cardContainer.appendChild(cardEl);

    // Update dashboard tab visibility — show only if ≥1 race
    this._updateTabVisibility();

    // If dashboard is active, update it
    if (this._activeTab === 'dashboard' && this._dashboardView) {
      this._dashboardView.update(this._latestRaces);
    }
  }

  getExportElement() {
    return this._cardContainer;
  }

  _updateTabVisibility() {
    const dashTab = this._tabBar?.querySelector('[data-tab="dashboard"]');
    if (dashTab) {
      dashTab.style.display = this._latestRaces.length > 0 ? '' : 'none';
    }
  }

  async _switchTab(tab) {
    this._activeTab = tab;

    // Update tab button styles
    this._tabBar.querySelectorAll('.preview-tabs__btn').forEach(btn => {
      btn.classList.toggle('preview-tabs__btn--active', btn.dataset.tab === tab);
    });

    if (tab === 'resume') {
      this._cardContainer.style.display = '';
      this._dashboardContainer.style.display = 'none';
    } else {
      this._cardContainer.style.display = 'none';
      this._dashboardContainer.style.display = '';

      // Lazy-create dashboard on first switch (dynamic import keeps Chart.js out of main bundle)
      try {
        if (!this._dashboardView) {
          const { DashboardView } = await import('../dashboard/DashboardView.js');
          this._dashboardView = new DashboardView();
          await this._dashboardView.render(this._dashboardContainer, this._latestRaces);
        } else {
          await this._dashboardView.update(this._latestRaces);
        }
      } catch (err) {
        console.error('Dashboard render failed:', err);
        const errDiv = document.createElement('div');
        errDiv.style.cssText = 'color:#ff6b9d;padding:24px;text-align:center;';
        errDiv.textContent = `Dashboard failed to load: ${err.message}`;
        this._dashboardContainer.innerHTML = '';
        this._dashboardContainer.appendChild(errDiv);
      }
    }
  }
}
